import React, { useEffect } from 'react';
import { ScrollView, TouchableOpacity, View } from 'react-native';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { translate } from '../../../services/translate';
import MyAppText from '../../../components/MyAppText';
import FontWeight from '../../../styles/fontWeights';
import TimePicker from 'react-time-picker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import Divider from '../../../components/Divider';
import FormSwitch from '../../../components/FormSwitch';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { WindowInformation } from '../../../services/window-information';


export interface ImageAlgorithmsForm {
    cameraId: string;
    services: {
        raining: boolean;
        person_detection: boolean;
        people_crowd: boolean;
    };
    times: AllowedTimes;
}

interface ImageAlgorithmsTabProps {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<ImageAlgorithmsForm, any, undefined>;
}


export default function ImageAlgorithmsTab({ form }: ImageAlgorithmsTabProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const times = useWatch({ control: form.control, name: 'times' });
    const services = useWatch({ control: form.control, name: 'services' });

    function renderDayIntervals(weekDay: number) {
        const dayTimes = times[weekDay] ?? [];

        return dayTimes.map((_, index) => {
            const interval = dayTimes[index];

            return (
                <View key={index} style={styles.timeIntervalContainer}>
                    <View style={{ maxWidth: 50 }}>
                        <MyAppText>{translate('start')}</MyAppText>
                        <TimePicker
                            disableClock={true}
                            format='HH:mm'
                            locale='pt-BR'
                            maxDetail='minute'
                            className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? 'react-time-picker__wrapper-light' : 'react-time-picker__wrapper-dark'}
                            value={interval.start}
                            clearIcon={null}
                            onChange={(value) => {
                                if (value) {
                                    form.setValue(`times.${weekDay}.${index}.start`, value, {
                                        shouldDirty: true,
                                    });
                                }
                            }}
                        />
                    </View>
                    <View style={{ maxWidth: 50 }}>
                        <MyAppText>{translate('end')}</MyAppText>
                        <TimePicker
                            disableClock={true}
                            clearIcon={null}
                            format='HH:mm'
                            locale='pt-BR'
                            maxDetail='minute'
                            className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? 'react-time-picker__wrapper-light' : 'react-time-picker__wrapper-dark'}
                            value={interval.end}
                            onChange={(value) => {
                                if (value) {
                                    form.setValue(`times.${weekDay}.${index}.end`, value, {
                                        shouldDirty: true,
                                    });
                                }
                            }}
                        />
                    </View>
                    <TouchableOpacity
                        onPress={() => {
                            const currentTimes = form.getValues('times');
                            const dayIntervals = [...(currentTimes[weekDay] ?? [])];

                            dayIntervals.splice(index, 1);

                            form.setValue(`times.${weekDay}`, dayIntervals, {
                                shouldDirty: true,
                            });
                        }}
                        style={{ paddingBottom: 3 }}
                    >
                        <FontAwesomeIcon fontSize={16} icon={faXmark} color={theme.colors.card.detailText} />
                    </TouchableOpacity>
                </View>
            );
        });
    }

    function addDayInterval(weekDay: number) {
        const currentTimes = form.getValues('times');
        const dayIntervals = [...(currentTimes[weekDay] ?? [])];

        dayIntervals.push({ start: '', end: '' });

        form.setValue(`times.${weekDay}`, dayIntervals, {
            shouldDirty: true,
        });
    }

    useEffect(() => {
        const enabledServices = Object.entries(services)
            .filter(([, enabled]) => enabled)
            .map(([service]) => service);

        if (enabledServices.length > 1) {
            const serviceToKeep = enabledServices[enabledServices.length - 1];

            for (const service of enabledServices) {
                if (service !== serviceToKeep) {
                    form.setValue(`services.${service as keyof ImageAlgorithmsForm['services']}`, false, { shouldDirty: true });
                }
            }
        }
    }, [services, form]);

    const renderWeekDay = (day: number, weekDay: string) => (
        <View style={{ flex: windowInfo.isMobile ? undefined : 1, gap: 8 }}>
            <MyAppText style={styles.weekDayText}>{translate(weekDay)}</MyAppText>
            {renderDayIntervals(day)}
            <TouchableOpacity onPress={() => addDayInterval(day)}>
                <MyAppText style={styles.addTimeLabel}>+ {translate('add')}</MyAppText>
            </TouchableOpacity>
        </View>
    );

    return (<>
        <View style={{ flexDirection: 'column', gap: 16, flex: 1 }}>
            {windowInfo.isMobile ? undefined : <MyAppText style={styles.description}>{translate('imageAlgorithmsExplanation')}</MyAppText>}
            <View style={{ flexDirection: 'column', rowGap: 8, zIndex: 2 }}>
                <MyAppText style={styles.sectionHeaderTitle}>{translate('imageAlgorithms')}</MyAppText>
                <Divider style={{ zIndex: -1 }} color={theme.colors.primary['200']} />
            </View>
            <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 16, zIndex: 2 }}>
                <View style={{ gap: 4, alignItems: 'flex-start' }}>
                    <MyAppText style={styles.switchHeader}>{translate('peopleCrowd')}</MyAppText>
                    <FormSwitch
                        label={translate('enabled')}
                        form={form}
                        fieldName='services.people_crowd'
                        viewStyle={styles.switchViewStyle}
                        labelStyle={styles.switchLabel}
                    />
                </View>
                <View style={{ gap: 4, alignItems: 'flex-start' }}>
                    <MyAppText style={styles.switchHeader}>{translate('raining')}</MyAppText>
                    <FormSwitch
                        label={translate('enabled')}
                        form={form}
                        fieldName='services.raining'
                        viewStyle={styles.switchViewStyle}
                        labelStyle={styles.switchLabel}
                    />
                </View>
                <View style={{ gap: 4, alignItems: 'flex-start' }}>
                    <MyAppText style={styles.switchHeader}>{translate('personDetection')}</MyAppText>
                    <FormSwitch
                        label={translate('enabled')}
                        form={form}
                        fieldName='services.person_detection'
                        viewStyle={styles.switchViewStyle}
                        labelStyle={styles.switchLabel}
                    />
                </View>
            </View>
            <ScrollView contentContainerStyle={{ flex: windowInfo.isMobile ? undefined : 1, flexDirection: windowInfo.isMobile ? 'column' : 'row', rowGap: 16, columnGap: 4 }}>
                {renderWeekDay(1, 'monday')}
                {renderWeekDay(2, 'tuesday')}
                {renderWeekDay(3, 'wednesday')}
                {renderWeekDay(4, 'thursday')}
                {renderWeekDay(5, 'friday')}
                {renderWeekDay(6, 'saturday')}
                {renderWeekDay(0, 'sunday')}
            </ScrollView>
        </View>
    </>);
}

const styleSheet = createStyleSheet(theme => ({
    weekDayText: {
        fontSize: 16,
        fontWeight: FontWeight.bold,
        color: theme.colors.neutral['500']
    },
    timeIntervalContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end'
    },
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    description: {
        color: theme.colors.neutral['400'],
        fontSize: 14,
    },
    switchViewStyle: {
        flexDirection: 'row-reverse',
        alignItems: 'center',
        gap: 8
    },
    switchLabel: {
        fontSize: 14,
        color: theme.colors.neutral['400']
    },
    switchHeader: {
        fontWeight: FontWeight.semiBold,
        color: theme.colors.neutral['500'],
        fontSize: 14
    },
    addTimeLabel: {
        color: theme.colors.secondaryButton.text,
        fontWeight: FontWeight.semiBold,
        fontSize: 16
    }
}));
